import React from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, LinearProgress,
  TextField, Tooltip, Typography, Switch, FormControlLabel,
  Divider
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ETVTable from '../tools/ETVTable';
import * as ETVConstants from '../ETVConstants';
import FloorWayfindingEditor from './FloorWayfindingEditor';
import {
  Delete, Edit, CheckCircle, Block, TvOutlined, Layers, Language,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import ConnectFloorWithDisplays from './ConnectFloorWithDisplays';
import ETVPopup from '../tools/ETVPopup';
import ETVActionButton from '../tools/ETVActionButton';
import { createFloorDirectoryHtml, createOverallFloorDirectoryHtml } from './FloorDirectoryHtml';
import DirectoryFormatPicker from './DirectoryFormatPicker';

class FloorMgmt extends React.Component {
  wayfindingEditorRef = React.createRef();
  connectedDevicesRef = React.createRef();

  state = {
    items: [],
    isLoaded: false,
    isLoading: false,
    error: null,
    floor_nodes:[],
    original_place_image_paths: [],
    session_uploaded_image_paths: [],

    window: '',
    info_title: '',
    info_message: '',

    action: '',
    floor_id: undefined,
    floor_label: '',
    floor_info: '',             // full string sent to backend (derived)
    floor_info_text: '',
    floor_info_entries: [],     // array for UI chips
    new_entry_text: '',         // input for adding a chip
    public_access: false,
    main_entry: false,

    // hidden position (not shown in UI, but sent on update to keep backend happy)
    floor_pos: 0,

    allDevices: [],
    selected_floor_id: undefined,
    pending_delete_floor: null,
    connected_devices_loading: false,
    connected_devices_has_changes: false,
    is_generating_directory: false,
    directory_generation_error: '',
    floor_directory_options_open: false,
    floor_directory_active_format: '',
    floor_directory_style: 'premium',
    is_generating_overall_directory: false,
    overall_directory_active_format: '',
    overall_directory_style: 'premium',
  };

  componentDidMount() {
    this.loadFloors();
    this.loadAllDisplays();
  }

  componentDidUpdate() {
    if (!this.state.isLoaded && !this.state.isLoading) {
      this.loadFloors();
    }
  }

  // ---------------- data loading ----------------
  loadAllDisplays() {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_devices_by_location&location_id=${this.props.location_id}`;
    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => this.setState({ allDevices: result.list || [] }),
        (error) => this.setState({ allDevices: [], error })
      );
  }

  loadFloors = () => {
    this.setState({ isLoading: true, error: null });
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_floors&location_id=${this.props.location_id}`;
    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => {
          this.setState({
            items: result.list || [],
            isLoaded: true,
            isLoading: false,
          });
        },
        (error) => {
          this.setState({
            items: [],
            isLoaded: false,
            isLoading: false,
            error,
          });
        }
      );
  };

  parseFloorInfo = (floorInfo) => {
    try {
      const parsed = typeof floorInfo === 'string'
        ? JSON.parse(floorInfo || '{}')
        : floorInfo;

      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch (err) {
      return {};
    }
  };

  parseFloorNodes = (floorInfo) => {
    const parsed = this.parseFloorInfo(floorInfo);
    return Array.isArray(parsed.nodes) ? parsed.nodes : [];
  };

  buildFloorInfo = () => {
    const existing = this.parseFloorInfo(this.state.floor_info);
    return JSON.stringify({
      ...existing,
      info: this.state.floor_info_text || '',
      nodes: this.state.floor_nodes || []
    });
  };

  openFloorDirectory = async (format) => {
    this.setState({ is_generating_directory: true, floor_directory_active_format: format, directory_generation_error: '' });
    try {
      const html = await createFloorDirectoryHtml(
        this.state.floor_label,
        this.state.floor_nodes,
        format,
        this.state.floor_directory_style
      );
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const link = document.createElement('a');
      const safeLabel = String(this.state.floor_label || 'floor').replace(/[^a-zA-Z0-9_-]/g, '_');
      link.href = url;
      link.download = `floor-directory-${safeLabel}.html`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.setState({
        is_generating_directory: false,
        floor_directory_active_format: '',
        floor_directory_options_open: false,
      });
    } catch {
      this.setState({
        is_generating_directory: false,
        floor_directory_active_format: '',
        directory_generation_error: ETVConstants.trans('floor_directory_generation_error'),
      });
    }
  };

  downloadOverallFloorDirectory = async (format) => {
    this.setState({ is_generating_overall_directory: true, overall_directory_active_format: format, directory_generation_error: '' });
    try {
      const html = await createOverallFloorDirectoryHtml(
        this.state.items,
        format,
        this.state.overall_directory_style
      );
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'floor-directory-all.html';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.setState({
        window: '',
        is_generating_overall_directory: false,
        overall_directory_active_format: '',
        directory_generation_error: '',
      });
    } catch {
      this.setState({
        is_generating_overall_directory: false,
        overall_directory_active_format: '',
        directory_generation_error: ETVConstants.trans('floor_directory_generation_error'),
      });
    }
  };

  getPlaceImagePaths = (nodes) => (nodes || [])
    .map(node => node.image_path)
    .filter(Boolean);

  deletePlaceImages = (paths) => {
    const uniquePaths = [...new Set((paths || []).filter(Boolean))];
    if (uniquePaths.length === 0) return Promise.resolve();
    const headers = {
      user_id: localStorage.getItem('etv.user_id'),
      access_token: localStorage.getItem('etv.access_token'),
    };
    return Promise.allSettled(uniquePaths.map(path => fetch(
      `${ETVConstants.getServerBase()}/place_image_upload?path=${encodeURIComponent(path)}`,
      { method: 'DELETE', headers }
    )));
  };

  closeFloorEditor = () => {
    this.deletePlaceImages(this.state.session_uploaded_image_paths);
    this.setState({ window: '', session_uploaded_image_paths: [] });
  };

  finishFloorSave = () => {
    const currentPaths = new Set(this.getPlaceImagePaths(this.state.floor_nodes));
    const pathsToDelete = [
      ...this.state.original_place_image_paths,
      ...this.state.session_uploaded_image_paths,
    ].filter(path => !currentPaths.has(path));
    return this.deletePlaceImages(pathsToDelete).then(() => this.setState({
      window: '',
      isLoaded: false,
      original_place_image_paths: [],
      session_uploaded_image_paths: [],
    }));
  };
  
  // ---------------- helpers ----------------
  getFloorLabel(floorID) {
    const id = String(floorID);
    const f = this.state.items.find(x => String(x.floor_id) === id);
    return f ? f.floor_label : '';
  }

  showWindow = (id, show) => this.setState({ window: show ? id : '' });
  showInfoMessage = (title, msg) => this.setState({ window: 'message_div', info_title: title, info_message: msg });
  closeInfoMessage = () => this.setState({ window: '', info_message: '' });
  ensureSuccess = (result) => {
    if (!result || result.result !== true) throw new Error(result?.data || 'request_failed');
    return result;
  };

  // Small helper to POST urlencoded
  postForm = (url, params) => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const body = new URLSearchParams(params);
    return fetch(url, {
      method: 'POST',
      headers: {
        'user_id': reqUserID,
        'access_token': accessToken,
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body,
    }).then(res => res.json());
  };

  // ---------------- floor CRUD ----------------
  addFloor = () => {
    this.setState({
      window: 'add_floor_div',
      action: 'add_floor',
      floor_id: undefined,
      floor_label: '',
      floor_info: '',
      floor_info_text: '',
      floor_info_entries: [],
      floor_nodes:[],
      original_place_image_paths: [],
      session_uploaded_image_paths: [],
      new_entry_text: '',
      public_access: false,
      main_entry: false,
      floor_pos: 0, // will compute next in save
      info_title: ETVConstants.trans('add_floor'),
    });
  };

  editFloor = (floor_id) => {
    const id = String(floor_id);
    const floor = this.state.items.find(f => String(f.floor_id) === id);
    if (!floor) return;

    const parsedFloorInfo = this.parseFloorInfo(floor.floor_info);
    const nodes = Array.isArray(parsedFloorInfo.nodes) ? parsedFloorInfo.nodes : [];

    this.setState({
      window: 'add_floor_div',
      action: 'update_floor',
      floor_id: floor.floor_id,
      floor_label: floor.floor_label || '',
      floor_info: floor.floor_info || '',
      floor_info_text: parsedFloorInfo.info || '',
      floor_nodes: nodes,
      original_place_image_paths: this.getPlaceImagePaths(nodes),
      session_uploaded_image_paths: [],
      public_access: !!floor.public_access,
      main_entry: !!floor.main_entry,
      floor_pos: Number(floor.floor_pos ?? 0),
      info_title: ETVConstants.trans('edit_floor'),
    });
  };

  createFloorDirectory = (floor) => {
    const parsedFloorInfo = this.parseFloorInfo(floor.floor_info);
    this.setState({
      floor_label: floor.floor_label || '',
      floor_nodes: Array.isArray(parsedFloorInfo.nodes) ? parsedFloorInfo.nodes : [],
      floor_directory_options_open: true,
      directory_generation_error: '',
    });
  };

  saveFloor = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const { floor_label, public_access, main_entry, items } = this.state;
    const floor_info = this.buildFloorInfo();
    //const floor_info = floor_info_entries.map(s => s.trim()).filter(Boolean).join('; ');
    const maxPos = Math.max(0, ...items.map(f => Number(f.floor_pos) || 0));
    const nextPos = maxPos + 1; // always append
    
    const url = `${ETVConstants.getServerBase()}/floor_access?action=add_floor`
      + `&location_id=${this.props.location_id}`
      + `&floor_label=${encodeURIComponent(floor_label)}`
      + `&floor_info=${encodeURIComponent(floor_info)}`
      + `&floor_pos=${nextPos}`                     // required by backend
      + `&public_access=${public_access}`
      + `&main_entry=${main_entry}`;          // "true"/"false"

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(this.ensureSuccess)
      .then(this.finishFloorSave)
      .catch(() => this.showInfoMessage('Error', 'Failed to save floor'));
  };

  updateFloor = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const { floor_id, floor_label, public_access, main_entry, floor_pos } = this.state;

    //const floor_info = floor_info_entries.map(s => s.trim()).filter(Boolean).join('; ');
    const floor_info = this.buildFloorInfo();

    const url = `${ETVConstants.getServerBase()}/floor_access?action=update_floor`
      + `&floor_id=${floor_id}`
      + `&floor_label=${encodeURIComponent(floor_label)}`
      + `&floor_info=${encodeURIComponent(floor_info)}`
      + `&floor_pos=${floor_pos}`                   // pass through unchanged
      + `&public_access=${public_access}`
      + `&main_entry=${main_entry}`;          // "true"/"false"

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(this.ensureSuccess)
      .then(this.finishFloorSave)
      .catch(() => this.showInfoMessage('Error', 'Failed to save floor'));
  };


  deleteFloor = (floorID) => {
    const url = `${ETVConstants.getServerBase()}/floor_access?action=delete_floor`;
    const params = { floor_id: floorID };
    const floor = this.state.items.find(item => String(item.floor_id) === String(floorID));
    const imagePaths = floor
      ? this.getPlaceImagePaths(this.parseFloorNodes(floor.floor_info))
      : [];

    this.postForm(url, params)
      .then(this.ensureSuccess)
      .then(() => this.deletePlaceImages(imagePaths))
      .then(() => this.setState({ window: '', isLoaded: false }))
      .catch(() => this.showInfoMessage('Error', 'Failed to delete floor'));
  };

  confirmDeleteFloor = (floor) => {
    this.setState({
      window: 'confirm_delete_floor',
      pending_delete_floor: floor,
    });
  };

  executeDeleteFloor = () => {
    const floorID = this.state.pending_delete_floor?.floor_id;
    if (floorID === undefined || floorID === null) return;
    this.deleteFloor(floorID);
  };

  editServingDisplays = (floorID) => {
    this.setState({
      selected_floor_id: floorID,
      window: 'edit_serving_displays',
      connected_devices_loading: true,
      connected_devices_has_changes: false,
    });
  };

  // ---------------- info entries helpers ----------------
  addInfoEntry = () => {
    const t = (this.state.new_entry_text || '').trim();
    if (!t) return;
    this.setState((s) => ({
      floor_info_entries: [...s.floor_info_entries, t],
      new_entry_text: ''
    }));
  };

  removeInfoEntry = (idx) => {
    this.setState((s) => ({
      floor_info_entries: s.floor_info_entries.filter((_, i) => i !== idx)
    }));
  };

  handleEntryKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.addInfoEntry();
    }
  };

  moveFloor = (floorID, direction) => {
    const sorted = [...this.state.items].sort(
      (a, b) => Number(a.floor_pos) - Number(b.floor_pos)
    );

    const index = sorted.findIndex(
      f => String(f.floor_id) === String(floorID)
    );

    const targetIndex = index + direction;

    // out of range (top moving up / bottom moving down)
    if (
      index < 0 ||
      targetIndex < 0 ||
      targetIndex >= sorted.length
    ) {
      return;
    }

    const current = sorted[index];
    const target = sorted[targetIndex];

    const currentPos = current.floor_pos;
    const targetPos = target.floor_pos;

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    const updatePosition = (floor, newPos) => {
      const floorInfo =
        typeof floor.floor_info === 'string'
          ? floor.floor_info
          : JSON.stringify(floor.floor_info);

      const url =
        `${ETVConstants.getServerBase()}/floor_access?action=update_floor`
        + `&floor_id=${floor.floor_id}`
        + `&floor_label=${encodeURIComponent(floor.floor_label)}`
        + `&floor_info=${encodeURIComponent(floorInfo)}`
        + `&floor_pos=${newPos}`
        + `&public_access=${floor.public_access}`
        + `&main_entry=${floor.main_entry}`;

      return fetch(url, {
        headers: {
          user_id: reqUserID,
          access_token: accessToken
        }
      }).then(res => res.json());
    };

    // swap positions
    Promise.all([
      updatePosition(current, targetPos),
      updatePosition(target, currentPos)
    ])
    .then(() => {
      this.setState({ isLoaded: false });
    })
    .catch(() => {
      this.showInfoMessage(
        'Error',
        'Failed to move floor'
      );
    });
  };


  // ---------------- render ----------------
  render() {
    const { items, isLoading, error } = this.state;

    const columnsDataFloor = [
      {
        title: '',
        field: '',
        width: 72,
        cellStyle: { width: 72, paddingLeft: 4, paddingRight: 2 },
        headerStyle: { width: 72, paddingLeft: 4, paddingRight: 2 },
        render: rd => {
          const sorted = [...this.state.items].sort(
            (a, b) => Number(a.floor_pos) - Number(b.floor_pos)
          );

          const index = sorted.findIndex(
            f => String(f.floor_id) === String(rd.floor_id)
          );

          const isTop = index === 0;
          const isBottom = index === sorted.length - 1;

          return (
            <Box sx={{ display: 'flex', gap: 0.125 }}>
              <IconButton
                size="small"
                disabled={isTop}
                onClick={() => this.moveFloor(rd.floor_id, -1)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                disabled={isBottom}
                onClick={() => this.moveFloor(rd.floor_id, 1)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </Box>
          );
        }
      },
      {
        title: ETVConstants.trans('floor_label'),
        field: 'floor_label',
        width: 110,
        cellStyle: { width: 110, paddingLeft: 4, paddingRight: 8 },
        headerStyle: { width: 110, paddingLeft: 4, paddingRight: 8 },
      },
      {
        title: ETVConstants.trans('floor_info'),
        field: 'floor_info',
        width: 180,
        cellStyle: { width: 180, paddingLeft: 8, paddingRight: 8 },
        headerStyle: { width: 180, paddingLeft: 8, paddingRight: 8 },
        render: rd => {
          const parsed = this.parseFloorInfo(rd.floor_info);
          return parsed.info || '';
        }
      },
      {
        title: ETVConstants.trans('public_access'),
        field: 'public_access',
        width: 120,
        render: rd => (
          rd.public_access
            ? <Chip size="small" color="success" icon={<CheckCircle />} label={ETVConstants.trans('public')} />
            : <Chip size="small" color="default" icon={<Block />} label={ETVConstants.trans('private')} />
        )
      },
      {
        title: ETVConstants.trans('main_entry'),
        field: 'main_entry',
        width: 105,
        render: rd => (
          rd.main_entry
            ? <Chip size="small" color="success" icon={<CheckCircle />} label={ETVConstants.trans('main_entry')} />
            : <span/>
        )
      },
      {
        title: 'directory',
        field: '',
        width: 82,
        cellStyle: { width: 82, paddingLeft: 5, paddingRight: 5, textAlign: 'center' },
        headerStyle: { width: 82, paddingLeft: 5, paddingRight: 5, textAlign: 'center' },
        render: rd => (
          <Tooltip title={ETVConstants.trans('generate_floor_directory')}>
            <IconButton color="primary" size="small" onClick={() => this.createFloorDirectory(rd)}>
              <Language fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
      {
        title: ETVConstants.trans('edit'),
        field: '',
        width: 58,
        cellStyle: { width: 58, paddingLeft: 5, paddingRight: 5, textAlign: 'center' },
        headerStyle: { width: 58, paddingLeft: 5, paddingRight: 5, textAlign: 'center' },
        render: rd => (
          <Tooltip title={ETVConstants.trans('edit')}>
            <IconButton color="primary" size="small" onClick={() => this.editFloor(rd.floor_id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
      {
        title: ETVConstants.trans('serving_displays'),
        field: '',
        width: 120,
        cellStyle: { width: 120, paddingLeft: 8, paddingRight: 8, textAlign: 'center' },
        headerStyle: { width: 120, paddingLeft: 8, paddingRight: 8, textAlign: 'center' },
        render: rd => (
          <Tooltip title={ETVConstants.trans('serving_displays')}>
            <IconButton color="primary" size="small" onClick={() => this.editServingDisplays(rd.floor_id)}>
              <TvOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
      {
        title: ETVConstants.trans('delete'),
        field: '',
        width: 62,
        cellStyle: { width: 62, paddingLeft: 5, paddingRight: 5, textAlign: 'center' },
        headerStyle: { width: 62, paddingLeft: 5, paddingRight: 5, textAlign: 'center' },
        render: rd => (
          <Tooltip title={ETVConstants.trans('delete')}>
            <IconButton color="error" size="small" onClick={() => this.confirmDeleteFloor(rd)}>
              <Delete />
            </IconButton>
          </Tooltip>
        )
      },
    ];

    return (
      <Box sx={{ width: '100%' }}>
          {isLoading && <LinearProgress sx={{ mb: 2 }} />}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {ETVConstants.trans('error') || 'Error'}: {String(error)}
              </Alert>
            )}

            <ETVTable
              data={items}
              columns={columnsDataFloor}
              title=""
              pageSize={10}
              search
              options={{
                padding: 'dense',
                tableLayout: 'fixed',
              }}
            />

        {/* Info dialog */}
        <ETVPopup
          open={this.state.window === 'confirm_delete_floor'}
          onClose={() => this.setState({ window: '', pending_delete_floor: null })}
          width="small"
          icon={<Delete color="error" />}
          title={ETVConstants.trans('delete_floor') || 'Delete floor'}
          description={ETVConstants.trans('confirm_delete') || 'Please confirm this action.'}
          bottomActions={
            <>
              <ETVActionButton
                action="cancel"
                onClick={() => this.setState({ window: '', pending_delete_floor: null })}
              />
              <Button
                color="error"
                variant="contained"
                startIcon={<Delete />}
                onClick={this.executeDeleteFloor}
              >
                {ETVConstants.trans('delete') || 'Delete'}
              </Button>
            </>
          }
        >
          <Typography>
            {(ETVConstants.trans('confirm_delete_floor') || 'Are you sure you want to delete this floor?')}
          </Typography>
          {this.state.pending_delete_floor?.floor_label && (
            <Typography sx={{ mt: 1 }} fontWeight={700}>
              {this.state.pending_delete_floor.floor_label}
            </Typography>
          )}
        </ETVPopup>

        <Dialog open={this.state.window === 'message_div'} onClose={this.closeInfoMessage} maxWidth="xs" fullWidth>
          <DialogTitle>{this.state.info_title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Typography variant="body1" component="span">{this.state.info_message}</Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeInfoMessage}>{ETVConstants.trans('close')}</Button>
          </DialogActions>
        </Dialog>

        <ETVPopup
          open={this.state.window === 'overall_directory_options'}
          onClose={() => this.showWindow('', false)}
          width="small"
          icon={<Language />}
          title={ETVConstants.trans('create_directory_image') || 'Create floor directory'}
          description={ETVConstants.trans('choose_directory_format')}
          bottomActions={
            <>
              <ETVActionButton
                action="cancel"
                disabled={this.state.is_generating_overall_directory}
                onClick={() => this.showWindow('', false)}
              />
            </>
          }
        >
          <Box sx={{ display: 'grid', gap: 2 }}>
            {this.state.directory_generation_error && (
              <Alert severity="error">{this.state.directory_generation_error}</Alert>
            )}
            <DirectoryFormatPicker
              disabled={this.state.is_generating_overall_directory}
              activeFormat={this.state.overall_directory_active_format}
              directoryStyle={this.state.overall_directory_style}
              onStyleChange={(overall_directory_style) => this.setState({ overall_directory_style })}
              onSelect={this.downloadOverallFloorDirectory}
            />
          </Box>
        </ETVPopup>

        {/* Serving displays dialog */}
        <ETVPopup
          open={this.state.window === 'edit_serving_displays'}
          onClose={() => this.showWindow('', false)}
          width="standard"
          icon={<TvOutlined />}
          title={ETVConstants.trans('serving_displays') || 'Connected devices'}
          description={ETVConstants.trans('desc_serving_floors')}
          loading={this.state.connected_devices_loading}
          bottomActions={
            <>
              <ETVActionButton action="cancel" label={ETVConstants.trans('close') || 'Close'} onClick={() => this.showWindow('', false)} />
              <ETVActionButton
                action="save"
                disabled={!this.state.connected_devices_has_changes || this.state.connected_devices_loading}
                onClick={() => this.connectedDevicesRef.current?.saveConnectedDisplays()}
              />
            </>
          }
        >
            {this.state.selected_floor_id ? (
              <ConnectFloorWithDisplays
                ref={this.connectedDevicesRef}
                floor_label={this.getFloorLabel(this.state.selected_floor_id)}
                floor_id={this.state.selected_floor_id}
                all_devices={this.state.allDevices}
                onStateChange={({ hasChanges, isLoading }) => this.setState({
                  connected_devices_has_changes: hasChanges,
                  connected_devices_loading: isLoading,
                })}
              />
            ) : null}
        </ETVPopup>

        {/* ADD / EDIT FLOOR dialog */}
        <ETVPopup
          open={this.state.window === 'add_floor_div'}
          onClose={this.closeFloorEditor}
          width="small"
          icon={this.state.action === 'add_floor' ? <Layers /> : <Edit />}
          title={this.state.info_title || ETVConstants.trans('edit_floor')}
          description={
            this.state.action === 'add_floor'
              ? (ETVConstants.trans('add_floor_description') || 'Define a new floor for this building.')
              : (ETVConstants.trans('edit_floor_description') || 'Update this floor and its wayfinding information.')
          }
          bottomActions={
            <>
              <ETVActionButton
                action="cancel"
                onClick={this.closeFloorEditor}
              />
              {this.state.action === 'add_floor' ? (
                <ETVActionButton action="save" onClick={this.saveFloor} />
              ) : (
                <ETVActionButton action="update" onClick={this.updateFloor} />
              )}
            </>
          }
        >
            <Box sx={{ display: 'grid', gap: 2.5 }}>
              {this.state.directory_generation_error && (
                <Alert severity="error">{this.state.directory_generation_error}</Alert>
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {ETVConstants.trans('floor_settings') || 'Floor settings'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {ETVConstants.trans('floor_settings_description') || 'Define the floor name and its access settings.'}
                </Typography>
              </Box>

              {/* Floor label */}
              <Typography variant="body2" color="text.secondary">
                {ETVConstants.trans('desc_floor_label')}
              </Typography>
              <TextField
                label={ETVConstants.trans('floor_label')}
                fullWidth
                value={this.state.floor_label}
                onChange={(e) => this.setState({ floor_label: e.target.value })}
              />
              <TextField
                label={ETVConstants.trans('floor_info')}
                fullWidth
                multiline
                minRows={2}
                value={this.state.floor_info_text}
                onChange={(e) => this.setState({ floor_info_text: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(this.state.public_access)}
                      onChange={(e) => this.setState({ public_access: e.target.checked })}
                    />
                  }
                  label={ETVConstants.trans('public_access')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(this.state.main_entry)}
                      onChange={(e) => this.setState({ main_entry: e.target.checked })}
                    />
                  }
                  label={ETVConstants.trans('main_entry') || 'Main entry'}
                />
              </Box>
              <Divider sx={{ my: 0.5 }} />

              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {ETVConstants.trans('wayfinding_nodes') || 'Wayfinding nodes'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {ETVConstants.trans('wayfinding_nodes_description') || 'Add the shops, offices, facilities, and access points available on this floor.'}
                </Typography>
              </Box>

              <Box sx={{ display: 'block' }}>
                <FloorWayfindingEditor
                  ref={this.wayfindingEditorRef}
                  nodes={this.state.floor_nodes}
                  onChange={(nodes) => this.setState({ floor_nodes: nodes })}
                  onImageUploaded={(path) => this.setState(s => ({
                    session_uploaded_image_paths: [...s.session_uploaded_image_paths, path]
                  }))}
                />
              </Box>
            </Box>
        </ETVPopup>

        <ETVPopup
          open={this.state.floor_directory_options_open}
          onClose={() => {
            if (!this.state.is_generating_directory) {
              this.setState({ floor_directory_options_open: false });
            }
          }}
          width="small"
          icon={<Language />}
          title={ETVConstants.trans('generate_floor_directory')}
          description={ETVConstants.trans('choose_directory_format')}
          bottomActions={
            <>
              <ETVActionButton
                action="cancel"
                disabled={this.state.is_generating_directory}
                onClick={() => this.setState({ floor_directory_options_open: false })}
              />
            </>
          }
        >
          <Box sx={{ display: 'grid', gap: 2 }}>
            {this.state.directory_generation_error && (
              <Alert severity="error">{this.state.directory_generation_error}</Alert>
            )}
            <DirectoryFormatPicker
              disabled={this.state.is_generating_directory}
              activeFormat={this.state.floor_directory_active_format}
              directoryStyle={this.state.floor_directory_style}
              onStyleChange={(floor_directory_style) => this.setState({ floor_directory_style })}
              onSelect={this.openFloorDirectory}
            />
          </Box>
        </ETVPopup>
      </Box>
    );
  }
}

export default FloorMgmt;
